/***
 *
 *   CLIENT ONBOARDING V2 SETTINGS — FORM STATE AND SAVE BODY
 *   Reads GET /api/client-onboarding-v2-settings into the settings page's form
 *   state, and turns that state into the body POST takes.
 *
 *   GET answers { available, data, prefill, form_valid, form_errors } at the top
 *   level, not under `data`, so the page reads it with Axios, not useAPI:
 *
 *     available    the ops flag (KTD14)
 *     data         the raw client_onboarding_v2 integration row, or null
 *     prefill      with no row and the flag on: the v1 settings as v2 settings,
 *                  where the first save starts (R3). Otherwise null.
 *     form_valid   the structure check on the saved form (R25): true, false, or
 *                  null when no form is saved
 *
 *   The server's updateIntegration replaces the row's `data` and `misc` whole.
 *   So the save body starts from what is stored (the row, or the prefill before
 *   the first save) and sets only the keys this page edits: a key it does not
 *   edit is sent back as it came. The server normaliser
 *   (helper/clientOnboardingV2.js, normaliseV2SettingsPayload) is the authority
 *   for every rule here; the page restates the rules so it never sends what the
 *   server would have to undo, and so the form shows what a save keeps.
 *
 *   Pure: no React, no network, so it is pinned by
 *   client-onboarding-v2-settings.helpers.test.js. Its copies of server
 *   constants (the template id, form marker, check type, relationship types,
 *   role defaults and standard field mappings) are pinned to the server by the
 *   "client drift" block in test/client-onboarding-v2-settings.js, which loads
 *   this file through Node's require(esm).
 *
 **********/

// The shared rules are CommonJS, because the server requires them too. An
// import, not require(), so that Node can load this file as an ES module for
// the drift block; the default import and the ".js" extension are what Node's
// ESM loader needs.
import rules from "../verification/client-onboarding-v2-rules.js";

const { ENTITY_TYPES, ROLES } = rules;

// The master v2 template. Mirrors CLIENT_ONBOARDING_V2_TEMPLATE_ID in
// bin/client-onboarding-v2-template.js. Choosing it makes the server install a
// fresh copy into the account (R24); the copy's id is what the row then keeps.
export const V2_TEMPLATE_ID = "96a80994-07bc-420d-ad23-0faf8119ca05";

// The v2 form marker, misc.type (KTD15).
export const V2_FORM_TYPE = "client_onboarding_v2";

export const V2_SETTINGS_URL = "/api/client-onboarding-v2-settings";

const SETTINGS_NAME = "Client Onboarding v2";

// Mirrors DEFAULT_CLIENT_ONBOARDING_CHECK_TYPE in helper/clientOnboardingCore.js.
export const DEFAULT_CHECK_TYPE = "kyc+tranche2_v1+sanctions+adverse_media";

export const CHECK_TYPE_OPTIONS = [
  { value: "kyc", label: "KYC Only" },
  { value: "kyc+sanctions", label: "KYC + PEP/Sanctions" },
  { value: "kyc+sanctions+adverse_media", label: "KYC + PEP/Sanctions + Adverse Media" },
  { value: "kyc+tranche2_v1", label: "KYC + Tranche 2" },
  { value: "kyc+tranche2_v1+sanctions", label: "KYC + Tranche 2 + PEP/Sanctions" },
  {
    value: "kyc+tranche2_v1+sanctions+adverse_media",
    label: "KYC + Tranche 2 + PEP/Sanctions + Adverse Media",
  },
];

// The nine organisation types, from the shared rules (KTD5). No "individual":
// in v2 a sole trader is a client person (R7).
export const V2_ENTITY_TYPES = ENTITY_TYPES.map((type) => ({
  value: type.value,
  label: type.text,
}));

const V2_ENTITY_TYPE_VALUES = V2_ENTITY_TYPES.map((type) => type.value);

// pms_sync.approval_mode (R21). Staff approval is the server's default too.
export const APPROVAL_MODES = [
  {
    value: "staff",
    label: "Staff approve first",
    description:
      "XPM sync waits until a staff member checks the XPM preview and approves the group.",
  },
  {
    value: "auto",
    label: "Sync at submit",
    description: "XPM records are created as soon as the client submits the form.",
  },
];

export const DEFAULT_APPROVAL_MODE = "staff";

const APPROVAL_MODE_VALUES = APPROVAL_MODES.map((mode) => mode.value);

// KTD9: the XPM group name always comes from the v2 form's group_name question.
const GROUP_NAME_FIELD = "group_name";

// Mirrors XPM_RELATIONSHIP_TYPES in helper/xpmClientGroups.js, for the choices
// only: the server screens every override against the same list.
export const XPM_RELATIONSHIP_TYPES = [
  "Director",
  "Shareholder",
  "Trustee",
  "Beneficiary",
  "Partner",
  "Settlor",
  "Associate",
  "Secretary",
  "Public Officer",
  "Husband",
  "Wife",
  "Spouse",
  "Parent Of",
  "Child Of",
  "Appointer",
  "Member",
  "Auditor",
  "Owner",
];

// The select value for "never send this role". Stored as null.
export const SKIP_RELATIONSHIP = "__skip__";

// Mirrors DEFAULT_RELATIONSHIP_TYPE_MAP in helper/xpmClientGroups.js for the
// v2 roles. A role missing here takes the server's fallback, Associate.
const DEFAULT_ROLE_XPM_TYPES = {
  trustee: "Trustee",
  director: "Director",
  shareholder: "Shareholder",
  partner: "Partner",
  beneficial_owner: "Owner",
  appointor: "Appointer",
  settlor: "Settlor",
  protector: "Associate",
  office_holder: "Associate",
  executor: "Associate",
  other: "Associate",
};

const RELATIONSHIP_FALLBACK_TYPE = "Associate";

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

// The role rows the v2 form offers (the shared rules), with the XPM type each
// gets when no override is stored.
export const V2_RELATIONSHIP_ROLES = ROLES.map((role) => ({
  value: role.value,
  label: role.text,
  default: hasOwn(DEFAULT_ROLE_XPM_TYPES, role.value)
    ? DEFAULT_ROLE_XPM_TYPES[role.value]
    : RELATIONSHIP_FALLBACK_TYPE,
}));

// The row types a v2 submission writes: each person is an `individual` row,
// and each entity a row of its type. The person mapping steers a client
// person's XPM client, and the Name, Email and Position of a contact person.
export const V2_FIELD_MAPPING_TYPES = [
  { value: "individual", label: "Person" },
  ...V2_ENTITY_TYPES,
];

// Mirrors DEFAULT_CLIENT_GROUP_ENTITY_MAPPINGS in helper/xpmClientMappings.js.
// A type with no stored mapping uses the server's copy, and this page stores
// nothing for it until someone edits it. So this copy is only what the form
// shows for an unedited type and where a first edit starts: if it drifts, a
// later edit stores the drifted copy, but an unedited type never does.
const ADDRESS_FIELDS = {
  City: "{suburb}",
  Region: "{state}",
  PostCode: "{postcode}",
  Country: "{country}",
};

const trustMapping = (structure, nameField, emailField, abnField) => ({
  Name: `{${nameField}}`,
  Email: `{${emailField}}`,
  Phone: "{phone}",
  BusinessStructure: structure,
  BusinessNumber: `{${abnField}}`,
  Address: "{registered_address}",
  ...ADDRESS_FIELDS,
});

const companyMapping = () => ({
  Name: "{company_name}",
  Email: "{company_email}",
  Phone: "{phone}",
  BusinessStructure: "Company",
  CompanyNumber: "{company_acn}",
  BusinessNumber: "{company_abn}",
  Address: "{registered_address}",
  ...ADDRESS_FIELDS,
});

export const STANDARD_FIELD_MAPPINGS = Object.freeze({
  individual: {
    Name: "{firstName} {lastName}",
    Email: "{email}",
    Phone: "{phone}",
    BusinessNumber: "{abn}",
    Mobile: "{mobile}",
    BusinessStructure: "Individual",
    FirstName: "{firstName}",
    LastName: "{lastName}",
    OtherName: "{middleName}",
    DateOfBirth: "{dob}",
    Address: "{residential_address}",
    ...ADDRESS_FIELDS,
  },
  trust: trustMapping("Trust", "trust_name", "contact_email", "trust_abn"),
  unit_trust: trustMapping("Trust", "trust_name", "contact_email", "trust_abn"),
  // v2 asks an SMSF its own questions, so its standard mapping reads them
  // (V2_DEFAULT_FIELD_MAPPINGS in helper/clientOnboardingV2.js).
  smsf: trustMapping("Trust", "smsf_name", "smsf_email", "smsf_abn"),
  body_corporate: companyMapping(),
  not_for_profit: companyMapping(),
  partnership: trustMapping("Partnership", "partnership_name", "partnership_email", "partnership_abn"),
  unincorporated_association: trustMapping(
    "Other",
    "association_name",
    "association_email",
    "association_abn"
  ),
  government_body: trustMapping("Other", "gov_entity_name", "gov_contact_email", "gov_abn"),
  deceased_estate: trustMapping("Trust", "estate_name", "estate_contact_email", "estate_abn"),
});

const { isPlainObject, asArray } = rules;

const asMap = (value) => (isPlainObject(value) ? value : {});

// A jsonb column arrives parsed from pg, but a text column or a test double can
// hand back the JSON string.
function parseMap(value) {
  if (typeof value === "string") {
    try {
      return asMap(JSON.parse(value));
    } catch (err) {
      return {};
    }
  }
  return asMap(value);
}

const stringSetting = (value) => (typeof value === "string" ? value.trim() : "");

// A subset of the nine types, in their own order. Anything else, "individual"
// included, is dropped.
function nineTypesOnly(list) {
  const chosen = asArray(list);
  return V2_ENTITY_TYPE_VALUES.filter((type) => chosen.includes(type));
}

/*
 * readSettingsResponse()
 * The GET body, made safe to read. `data` is what a save starts from: the
 * row's data, or the prefill before the first save.
 */
export function readSettingsResponse(body) {
  const response = asMap(body);
  const row = isPlainObject(response.data) ? response.data : null;
  const available = response.available === true;
  return {
    available,
    row,
    hasRow: Boolean(row),
    // R2: with the flag off, only an account that already has a v2 row may use
    // the page.
    notEnabled: !available && !row,
    data: row ? parseMap(row.data) : parseMap(response.prefill),
    misc: row ? parseMap(row.misc) : {},
    formValid: typeof response.form_valid === "boolean" ? response.form_valid : null,
    formErrors: asArray(response.form_errors).filter(isPlainObject),
  };
}

// The v1 settings page offers a link to v2 when this account can use it.
export function v2SettingsLinkAvailable(body) {
  return !readSettingsResponse(body).notEnabled;
}

/*
 * formStateFromSettings()
 * The settings page's form state, from the GET body.
 *
 *   - autoVerify reads verification.auto_verify on its own: checks go out at
 *     submit whatever XPM auto-create does (R15).
 *   - enabledEntityTypes is a subset of the nine types. No stored list means
 *     every type, as the server reads it.
 *   - writeTfn shows what a save keeps. Only an administrator may set it, and
 *     a non-admin save carries the stored value; before the first save nothing
 *     is stored, so for a non-admin it shows off.
 *   - formId is the saved form, else the v2 template while the flag is on.
 *     The account's forms load after the settings, so before the first save
 *     the page then moves it to the account's newest v2 copy, if there is one
 *     (defaultFormId with the forms).
 */
export function formStateFromSettings(body, { mayWriteTfn = false } = {}) {
  const source = readSettingsResponse(body);
  const { data, misc, row, hasRow, available } = source;
  const verification = asMap(data.verification);
  const pmsSync = asMap(data.pms_sync);
  const autoCreate = pmsSync.auto_create === true;
  const storedWriteTfn = autoCreate && asMap(pmsSync.write_tfn).enabled === true;

  return {
    enabled: hasRow ? row.enabled === true : data.enabled === true,
    notificationEmail: stringSetting(data.notification_email),
    redirectUrl: stringSetting(data.redirect_url),
    formId: defaultFormId({ savedFormId: misc.form_id, available }),
    enabledEntityTypes: Array.isArray(data.enabled_entity_types)
      ? nineTypesOnly(data.enabled_entity_types)
      : [...V2_ENTITY_TYPE_VALUES],
    checkType: stringSetting(verification.check_type) || DEFAULT_CHECK_TYPE,
    autoVerify: verification.auto_verify === true,
    provider: stringSetting(pmsSync.provider),
    autoCreate,
    approvalMode: APPROVAL_MODE_VALUES.includes(pmsSync.approval_mode)
      ? pmsSync.approval_mode
      : DEFAULT_APPROVAL_MODE,
    addRelationships: autoCreate && pmsSync.add_relationships === true,
    relationshipTypeMap: { ...asMap(pmsSync.relationship_type_map) },
    fieldMappings: { ...asMap(pmsSync.field_mappings) },
    writeTfn: hasRow || mayWriteTfn ? storedWriteTfn : false,
  };
}

/*
 * buildV2SettingsPayload()
 * The POST body: { enabled, name, data, misc }. Starts from what is stored and
 * sets only the keys the page edits, so every other key, at every level, is
 * sent back as it came (updateIntegration replaces `data` and `misc` whole).
 *
 *   - Auto-create, relationships and the TFN write need XPM as the provider.
 *   - group_name_field is always group_name (KTD9).
 *   - write_tfn is sent only by an administrator. A non-admin body leaves it
 *     out, so the server carries the stored value forward and the page can
 *     never send write_tfn.enabled: true for someone who may not set it.
 *   - misc keeps the stored keys; form_id is the chosen form.
 *   - loaded_without_row: true when the page loaded with no v2 row. Another
 *     tab's first save may have stored the row and its template copy since;
 *     the marker lets the server keep that copy when this save still names
 *     the template, the first-save default. The server never stores it.
 */
export function buildV2SettingsPayload(state, body, { mayWriteTfn = false } = {}) {
  const form = asMap(state);
  const { data: stored, misc: storedMisc, hasRow } = readSettingsResponse(body);
  const storedPmsSync = asMap(stored.pms_sync);
  const enabled = form.enabled === true;
  const xpmAutoCreate = form.provider === "xpm" && form.autoCreate === true;

  const pmsSync = {
    ...storedPmsSync,
    provider: stringSetting(form.provider),
    auto_create: xpmAutoCreate,
    approval_mode: APPROVAL_MODE_VALUES.includes(form.approvalMode)
      ? form.approvalMode
      : DEFAULT_APPROVAL_MODE,
    group_name_field: GROUP_NAME_FIELD,
    add_relationships: xpmAutoCreate && form.addRelationships === true,
    relationship_type_map: { ...asMap(form.relationshipTypeMap) },
    field_mappings: { ...asMap(form.fieldMappings) },
  };
  if (mayWriteTfn) {
    pmsSync.write_tfn = { enabled: xpmAutoCreate && form.writeTfn === true };
  } else {
    delete pmsSync.write_tfn;
  }

  const formId = stringSetting(form.formId);
  return {
    enabled,
    name: SETTINGS_NAME,
    data: {
      ...stored,
      enabled,
      notification_email: stringSetting(form.notificationEmail),
      redirect_url: stringSetting(form.redirectUrl),
      enabled_entity_types: nineTypesOnly(form.enabledEntityTypes),
      verification: {
        ...asMap(stored.verification),
        check_type: stringSetting(form.checkType) || DEFAULT_CHECK_TYPE,
        auto_verify: form.autoVerify === true,
      },
      pms_sync: pmsSync,
    },
    misc: { ...storedMisc, ...(formId ? { form_id: formId } : {}) },
    ...(hasRow ? {} : { loaded_without_row: true }),
  };
}

// Ticks or unticks one of the nine types. Anything else changes nothing.
export function toggleEntityType(list, type) {
  const current = nineTypesOnly(list);
  if (!V2_ENTITY_TYPE_VALUES.includes(type)) return current;
  return current.includes(type)
    ? current.filter((value) => value !== type)
    : nineTypesOnly([...current, type]);
}

// The select value for one role: its override, skip, or its default.
export function relationshipTypeFor(map, role) {
  const overrides = asMap(map);
  if (!hasOwn(overrides, role.value)) return role.default;
  if (overrides[role.value] === null) return SKIP_RELATIONSHIP;
  return overrides[role.value] || role.default;
}

// Stores only real overrides, so a later change to a server default is picked
// up rather than frozen in settings. Skip is stored as null.
export function setRelationshipType(map, roleValue, value) {
  const next = { ...asMap(map) };
  const role = V2_RELATIONSHIP_ROLES.find((r) => r.value === roleValue);
  const fallback = role ? role.default : RELATIONSHIP_FALLBACK_TYPE;
  if (value === SKIP_RELATIONSHIP) {
    next[roleValue] = null;
  } else if (value === fallback) {
    delete next[roleValue];
  } else {
    next[roleValue] = value;
  }
  return next;
}

/*
 * Fold a legacy `TaxNumber` key into `BusinessNumber`, for display. Mirrors
 * collapseXpmTaxNumberKey (helper/xpmClientMappings.js), the copy that matters:
 * the server folds on save and on the way to XPM. Same rule as the v1 page.
 */
function collapseTaxNumberKey(map) {
  if (!hasOwn(map, "TaxNumber")) return map;
  const { TaxNumber, ...rest } = map;
  if (!String(TaxNumber || "").trim()) return rest;
  if (!String(rest.BusinessNumber || "").trim()) return { ...rest, BusinessNumber: TaxNumber };
  // Two different templates for one field: keep both so a human decides.
  return rest.BusinessNumber === TaxNumber ? rest : map;
}

const standardMappingFor = (type) =>
  hasOwn(STANDARD_FIELD_MAPPINGS, type) ? STANDARD_FIELD_MAPPINGS[type] : {};

// One row type's mapping for display. `customised` is false when nothing is
// stored, so the server's standard mapping applies. Own keys only: a lookup
// of "constructor" must not find a function.
export function fieldMappingFor(fieldMappings, type) {
  const stored = asMap(fieldMappings);
  if (hasOwn(stored, type) && isPlainObject(stored[type])) {
    return { mappings: collapseTaxNumberKey(stored[type]), customised: true };
  }
  return { mappings: standardMappingFor(type), customised: false };
}

// Sets one XPM field's template. The first edit of a type starts from the
// standard mapping, so the other fields keep their values.
export function setFieldMapping(fieldMappings, type, pmsField, value) {
  const current = fieldMappingFor(fieldMappings, type).mappings;
  return { ...asMap(fieldMappings), [type]: { ...current, [pmsField]: value } };
}

// Removes one type's stored mapping, so the server's standard mapping applies.
export function resetFieldMapping(fieldMappings, type) {
  const next = { ...asMap(fieldMappings) };
  delete next[type];
  return next;
}

// The account's v2 forms, from GET /api/form. Never the template itself.
function accountV2Forms(forms) {
  return asArray(forms)
    .filter((f) => isPlainObject(f) && f.id && f.id !== V2_TEMPLATE_ID)
    .filter((f) => parseMap(f.misc).type === V2_FORM_TYPE);
}

// When a form was last changed. A form with no readable date sorts oldest.
function lastChanged(form) {
  const time = Date.parse(form.updated_at || form.created_at);
  return Number.isNaN(time) ? -Infinity : time;
}

/*
 * defaultFormId()
 * Where the form picker starts: the saved form. Before the first save, the
 * account's most recently changed v2 copy, for example one installed from the
 * Form catalog and then customised (KTD15 allows that while the flag is on),
 * so a first save left unchanged keeps that copy and does not install a new,
 * uncustomised one. Only with no copy, the template while the flag is on.
 */
export function defaultFormId({ forms = [], savedFormId = "", available = false } = {}) {
  const saved = stringSetting(savedFormId);
  if (saved) return saved;
  const newest = accountV2Forms(forms).reduce(
    (best, f) => (!best || lastChanged(f) > lastChanged(best) ? f : best),
    null
  );
  if (newest) return newest.id;
  return available ? V2_TEMPLATE_ID : "";
}

/*
 * formChoices()
 * The form picker: the account's v2 forms, the saved form when the list does
 * not have it, and, while the flag is on, a fresh copy of the template (the
 * server refuses the template while the flag is off).
 */
export function formChoices({ forms = [], savedFormId = "", available = false } = {}) {
  const choices = accountV2Forms(forms)
    .map((f) => ({
      value: f.id,
      label: f.id === savedFormId ? `${f.name} (current)` : f.name,
    }));
  if (
    savedFormId &&
    savedFormId !== V2_TEMPLATE_ID &&
    !choices.some((choice) => choice.value === savedFormId)
  ) {
    choices.unshift({ value: savedFormId, label: "Current form" });
  }
  if (available) {
    choices.push({
      value: V2_TEMPLATE_ID,
      label: "Install a new copy of the Client Onboarding v2 template",
    });
  }
  return choices;
}

// The public v2 link (KTD1): its own path, beside v1's /client-onboarding/:id.
export function v2PublicUrl(origin, rowId) {
  return rowId ? `${origin}/client-onboarding-v2/${rowId}` : null;
}

export const REDIRECT_URL_ERROR =
  "The redirect URL must be a full web address that starts with https:// or http://.";

/*
 * redirectUrlError()
 * "" when the redirect is blank or an absolute http(s) URL, else why not. The
 * public view sends the client to it, so the server keeps only those
 * (normaliseV2SettingsPayload) and drops anything else, a javascript: URL or a
 * relative path included. The page stops such a save and says why, rather
 * than let the value go without a word.
 */
export function redirectUrlError(value) {
  const url = stringSetting(value);
  if (!url) return "";
  if (!/^https?:\/\//i.test(url)) return REDIRECT_URL_ERROR;
  try {
    return new URL(url).hostname ? "" : REDIRECT_URL_ERROR;
  } catch {
    return REDIRECT_URL_ERROR;
  }
}

/*
 * runExclusive()
 * Runs `task` unless a run started through the same flag ({ current }) is
 * still in flight, and answers whether it ran. The page's save posts the whole
 * row built from the response it last loaded, so a second click while a save
 * is in flight posts the same pre-save body. The server lock
 * (integration.withAccountTypeLock) already keeps a first save to one v2 row
 * and one template copy, so this flag is defence in depth: it saves a
 * redundant POST and a second notification. The shared Button has no disabled
 * state, so the flag is the page's guard. The page holds it in a ref, set at
 * once, so a second click cannot pass it before React re-renders.
 */
export async function runExclusive(flag, task) {
  if (flag.current) return false;
  flag.current = true;
  try {
    await task();
    return true;
  } finally {
    flag.current = false;
  }
}

/*
 * saveMayHaveStored()
 * A failed save that may still have reached the database: no reply at all
 * (network error, timeout) or a 5xx, because a proxy 502/504 can arrive after
 * the server stored the row. A 4xx is a refusal, so nothing was stored.
 */
export function saveMayHaveStored(err) {
  const status = err?.response?.status;
  return !status || status >= 500;
}

/*
 * formIdAfterUnclearSave()
 * The picker value after a save that may have stored. While the picker still
 * holds the master template and the server now holds a saved copy, it moves to
 * that copy, so the next save does not install a second one. Any other pick is
 * the user's and stands.
 */
export function formIdAfterUnclearSave(formId, body) {
  const storedFormId = readSettingsResponse(body).misc.form_id;
  return formId === V2_TEMPLATE_ID && storedFormId ? storedFormId : formId;
}
